/**
 * vehicle-finder-facets.js
 * Drop-in replacement:
 * - When a Make is selected, we load Model/Year/Category facet values from the make-specific collection
 *   (e.g. /collections/bmw) to avoid Shopify's global facet value truncation on /collections/all.
 * - Still supports the original base collection URL for makes list + fallback behavior.
 */
(function () {
  const DEFAULT_SECTION_IDS = ["main-collection-product-grid", "main-collection", "collection-main"];

  function enc(v) {
    return encodeURIComponent(String(v ?? "").trim());
  }

  function buildUrl(base, params) {
    const parts = Object.entries(params)
      .filter(([, v]) => String(v || "").trim() !== "")
      .map(([k, v]) => `${enc(k)}=${enc(v)}`);
    if (!parts.length) return base;
    return `${base}${base.includes("?") ? "&" : "?"}${parts.join("&")}`;
  }

  async function fetchCollectionHtml(url, forceFull = false) {
    if (!forceFull) {
      for (const id of DEFAULT_SECTION_IDS) {
        try {
          const u = url + (url.includes("?") ? "&" : "?") + `section_id=${encodeURIComponent(id)}`;
          const res = await fetch(u, { credentials: "same-origin" });
          if (!res.ok) continue;
          const html = await res.text();
          if (html && html.length > 100) return html;
        } catch (_) {}
      }
    }

    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) throw new Error("Failed to fetch collection HTML");
    return await res.text();
  }

  function extractFilterValues(html, key) {
    if (!key) return [];
    const doc = new DOMParser().parseFromString(html, "text/html");

    const inputs = Array.from(doc.querySelectorAll(`input[name="${CSS.escape(key)}"]`));
    const fromInputs = inputs.map((i) => i.value).filter(Boolean);

    const selects = Array.from(doc.querySelectorAll(`select[name="${CSS.escape(key)}"]`));
    const fromSelects = selects.flatMap((s) =>
      Array.from(s.querySelectorAll("option"))
        .filter((o) => o.value)
        .map((o) => o.value)
    );

    const links = Array.from(doc.querySelectorAll(`a[href*="${encodeURIComponent(key)}="]`));
    const fromLinks = links
      .map((a) => {
        try {
          const u = new URL(a.getAttribute("href"), window.location.origin);
          return u.searchParams.get(key);
        } catch {
          return null;
        }
      })
      .filter(Boolean);

    const uniq = Array.from(new Set([...fromInputs, ...fromSelects, ...fromLinks]));

    const allYears = uniq.length > 0 && uniq.every((v) => /^\d{4}$/.test(v));
    if (allYears) uniq.sort((a, b) => Number(a) - Number(b));
    else uniq.sort((a, b) => a.localeCompare(b));

    return uniq;
  }

  function setSelectOptions(selectEl, values, placeholder, preferredValue = null) {
    if (!selectEl) return;

    const keep = preferredValue ?? selectEl.value;

    selectEl.innerHTML = "";
    const ph = document.createElement("option");
    ph.value = "";
    ph.textContent = placeholder;
    selectEl.appendChild(ph);

    values.forEach((v) => {
      const opt = document.createElement("option");
      opt.value = v;
      opt.textContent = v;
      selectEl.appendChild(opt);
    });

    if (keep && values.includes(keep)) selectEl.value = keep;
  }

  // IMPORTANT: do NOT wipe options during loading (that was causing resets)
  function setLoading(selectEl, loadingText) {
    if (!selectEl) return;
    selectEl.disabled = true;

    // update placeholder text if present, without destroying current options/value
    const ph = selectEl.querySelector('option[value=""]');
    if (ph) ph.textContent = loadingText;
  }

  function clearAndDisable(selectEl, placeholder) {
    if (!selectEl) return;
    selectEl.disabled = true;
    setSelectOptions(selectEl, [], placeholder, null);
    selectEl.value = "";
  }

  // --- Make -> Collection handle helpers (NEW) ---
  function slugifyHandle(str) {
    return String(str || "")
      .trim()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  async function collectionExists(path) {
    try {
      const res = await fetch(path, { method: "HEAD", credentials: "same-origin" });
      return res.ok;
    } catch {
      return false;
    }
  }

  async function initFinder(root) {
    const base = root.dataset.collectionUrl || "/collections/all";

    const keyMake = root.dataset.keyMake || "";
    const keyModel = root.dataset.keyModel || "";
    const keyYear = root.dataset.keyYear || ""; // optional
    const keyType = root.dataset.keyType || ""; // optional

    const makeSel = root.querySelector(".vehicle-finder__make");
    const modelSel = root.querySelector(".vehicle-finder__model");
    const yearSel = root.querySelector(".vehicle-finder__year");
    const typeSel = root.querySelector(".vehicle-finder__type");
    const btn = root.querySelector(".vehicle-finder__submit");

    if (!makeSel || !modelSel || !btn) return;

    let suppress = false;

    function updateButtonState() {
      btn.disabled = !(makeSel.value && modelSel.value);
    }

    function currentParams(includeYear = true, includeType = true) {
      const params = { [keyMake]: makeSel.value, [keyModel]: modelSel.value };
      if (includeYear && yearSel && keyYear && yearSel.value) params[keyYear] = yearSel.value;
      if (includeType && typeSel && keyType && typeSel.value) params[keyType] = typeSel.value;
      return params;
    }

    // NEW: choose the most reliable base for the selected make
    // - Prefer /collections/<make-handle> if it exists
    // - Else fall back to original base (usually /collections/all)
    async function baseForMake(make) {
      if (!make) return base;

      const handle = slugifyHandle(make);
      const makeCollection = `/collections/${handle}`;

      if (await collectionExists(makeCollection)) return makeCollection;
      return base;
    }

    async function refreshSelectPreserve({ selectEl, key, placeholder, url }) {
      if (!selectEl || !key) return { kept: true };

      const keep = selectEl.value; // remember BEFORE fetch/rebuild

      let html = await fetchCollectionHtml(url);
      let values = extractFilterValues(html, key);
      if (!values.length) {
        html = await fetchCollectionHtml(url, true);
        values = extractFilterValues(html, key);
      }

      suppress = true;
      setSelectOptions(selectEl, values, placeholder, keep);
      selectEl.disabled = values.length === 0;

      // If the kept value is NOT in the new list, clear it
      if (keep && !values.includes(keep)) selectEl.value = "";

      suppress = false;
      return { kept: !keep || values.includes(keep) };
    }

    async function refreshYears() {
      if (!yearSel || !keyYear) return;
      const make = makeSel.value,
        model = modelSel.value;
      if (!make || !model) return;

      const scopedBase = await baseForMake(make);

      // Years respect category if selected
      const url = buildUrl(scopedBase, currentParams(false, true));

      await refreshSelectPreserve({
        selectEl: yearSel,
        key: keyYear,
        placeholder: "Select year…",
        url,
      });
    }

    async function refreshTypes() {
      if (!typeSel || !keyType) return;
      const make = makeSel.value,
        model = modelSel.value;
      if (!make || !model) return;

      const scopedBase = await baseForMake(make);

      // Types respect year if selected
      const url = buildUrl(scopedBase, currentParams(true, false));

      await refreshSelectPreserve({
        selectEl: typeSel,
        key: keyType,
        placeholder: "Select category…",
        url,
      });
    }

    // Initial UI
    setLoading(makeSel, "Loading makes…");
    clearAndDisable(modelSel, "Select model…");
    if (yearSel) clearAndDisable(yearSel, "Select year…");
    if (typeSel) clearAndDisable(typeSel, "Select category…");
    btn.disabled = true;

    async function loadMakes() {
      // Keep makes list based on the global base collection (your current behavior)
      // If you ever want makes to ignore current filtering entirely, keep this as /collections/all.
      const html = await fetchCollectionHtml(base);
      const makes = extractFilterValues(html, keyMake);

      suppress = true;
      setSelectOptions(makeSel, makes, "Select make…");
      makeSel.disabled = false;
      suppress = false;
    }

    async function loadModels(make) {
      // NEW: load models from make-specific collection if it exists (reliable full list)
      const scopedBase = await baseForMake(make);
      const html = await fetchCollectionHtml(scopedBase);
      const models = extractFilterValues(html, keyModel);

      suppress = true;
      setSelectOptions(modelSel, models, "Select model…");
      modelSel.disabled = models.length === 0;
      suppress = false;
    }

    makeSel.addEventListener("change", async () => {
      if (suppress) return;

      const make = makeSel.value;

      setLoading(modelSel, make ? "Loading models…" : "Select model…");
      if (yearSel) clearAndDisable(yearSel, "Select year…");
      if (typeSel) clearAndDisable(typeSel, "Select category…");
      btn.disabled = true;

      if (!make) {
        clearAndDisable(modelSel, "Select model…");
        return;
      }

      await loadModels(make);
      updateButtonState();
    });

    modelSel.addEventListener("change", async () => {
      if (suppress) return;

      const make = makeSel.value;
      const model = modelSel.value;

      if (yearSel) clearAndDisable(yearSel, "Select year…");
      if (typeSel) clearAndDisable(typeSel, "Select category…");
      updateButtonState();

      if (!make || !model) return;

      // Load both optional lists (unconstrained by each other initially)
      if (yearSel && keyYear) setLoading(yearSel, "Loading years…");
      if (typeSel && keyType) setLoading(typeSel, "Loading categories…");

      await Promise.all([refreshYears(), refreshTypes()]);
      updateButtonState();
    });

    // Category change: refresh years, but KEEP year if still valid
    if (typeSel) {
      typeSel.addEventListener("change", async () => {
        if (suppress) return;

        if (yearSel && keyYear) {
          setLoading(yearSel, "Loading years…");
          await refreshYears();
        }

        updateButtonState();
      });
    }

    // Year change: refresh categories, but KEEP category if still valid
    if (yearSel) {
      yearSel.addEventListener("change", async () => {
        if (suppress) return;

        if (typeSel && keyType) {
          setLoading(typeSel, "Loading categories…");
          await refreshTypes();
        }

        updateButtonState();
      });
    }

    btn.addEventListener("click", () => {
      if (btn.disabled) return;

      // NEW: redirect to make-specific collection when possible
      // This ensures the landing page also has complete model facet options.
      baseForMake(makeSel.value).then((scopedBase) => {
        window.location.assign(buildUrl(scopedBase, currentParams(true, true)));
      });
    });

    await loadMakes();
  }

  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll(".vehicle-finder").forEach((el) => {
      initFinder(el).catch((e) => console.warn("Vehicle Finder init failed:", e));
    });
  });
})();
